import { inject, Injectable } from '@angular/core';
import { StatService } from '@cargos/sprintpay-services';
import { map, Observable, of, shareReplay, startWith } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface MaintenanceCopy {
    title: string;
    appName: string;
    message: string;
    badgeLabel: string;
    dateValue: string;
    statusValue: string;
    returnValue: string;
    footerText: string;
    contactUrl: string;
}

@Injectable({
    providedIn: 'root',
})
export class MaintenanceService {
    private static readonly TITLE_FALLBACK = "We'll Be Back Soon";
    private static readonly MESSAGE_FALLBACK =
        'SprintPay is currently unavailable due to scheduled maintenance. Our team is working to restore service as quickly as possible.';
    private static readonly APP_NAME_FALLBACK = 'SprintPay';
    private static readonly STATUS_FALLBACK = 'Planned system maintenance is currently in progress.';
    private static readonly FOOTER_FALLBACK = 'Thank you for your patience and understanding.';
    private static readonly CONTACT_URL_FALLBACK = 'https://cargosprint.com/contact/';
    private static readonly BADGE_FALLBACK = 'Scheduled Maintenance';

    private readonly statService = inject(StatService, { optional: true });

    /**
     * Confirmed maintenance state, emitted only *after* the Statsig SDK is
     * ready. One-shot: emits once (gate value or fallback on timeout) and
     * completes. Use this to gate side effects (script injection, network
     * calls) that must not run under maintenance.
     */
    public readonly maintenanceReady$: Observable<boolean> = (
        this.statService?.isFeatureEnabledWhenReady(environment.maintenance.gateName) ?? of(false)
    ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

    /**
     * Emits `false` immediately (via `startWith`) to prevent a flash of the
     * loading placeholder, then re-emits with the confirmed gate value once
     * the SDK is ready. Meant for the view only.
     */
    public readonly maintenance$: Observable<boolean> = this.maintenanceReady$.pipe(startWith(false));

    /**
     * Copy for the maintenance overlay, resolved *after* the Statsig SDK is
     * ready (isFeatureEnabledWhenReady awaits `waitUntilReady` before
     * emitting). Reading the Dynamic Config synchronously in a field
     * initializer returns the fallback because the client hasn't loaded yet
     * — deriving it from `maintenance$` guarantees the real values are used.
     */
    public readonly maintenanceCopy$: Observable<MaintenanceCopy> = this.maintenance$.pipe(
        map(() => ({
            title: this.read('title', MaintenanceService.TITLE_FALLBACK),
            appName: this.read('appName', MaintenanceService.APP_NAME_FALLBACK),
            message: this.read('message', MaintenanceService.MESSAGE_FALLBACK),
            badgeLabel: this.read('badgeLabel', MaintenanceService.BADGE_FALLBACK),
            dateValue: this.read('dateValue', ''),
            statusValue: this.read('statusValue', MaintenanceService.STATUS_FALLBACK),
            returnValue: this.read('returnValue', ''),
            footerText: this.read('footerText', MaintenanceService.FOOTER_FALLBACK),
            contactUrl: this.read('contactUrl', MaintenanceService.CONTACT_URL_FALLBACK),
        }))
    );

    private read<T>(key: string, fallback: T): T {
        return (
            this.statService?.getDynamicConfigValue<T>(environment.maintenance.dynamicConfigName, key, fallback) ??
            fallback
        );
    }
}
